import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LogoDark2x from "../../images/logo-dark2x.png";
import { useDispatch } from "react-redux";
import authService from "../../api/auth/authService";
import MfaLoginStep from "./MfaLoginStep";
import { fetchCurrentUser, setAuthenticated } from "../../store/slices/misc/currentUser";
import { AppDispatch } from "../../store/store";

/**
 * Password-reset landing page for the link in the reset email
 * (route: /auth/verifyToken?access_token=…). Reads the token, lets the user set a
 * new password, and calls the reset endpoint.
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [params] = useSearchParams();
  const token = params.get("access_token") || params.get("token") || "";
  const invite = params.get("invite") === "1";   // first password of a newly invited user
  // after the password is saved the user is signed in; two-step verification (QR) follows on this page
  const [mfa, setMfa] = useState<{ mfaToken: string; setupRequired: boolean; email: string } | null>(null);
  const tokenEmail = useMemo(() => { try { return String(JSON.parse(atob(token.split(".")[1])).email || ""); } catch { return ""; } }, [token]);

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const tokenExpired = useMemo(() => {
    try {
      const p = JSON.parse(atob(token.split(".")[1]));
      return p.exp ? p.exp * 1000 < Date.now() : false;
    } catch { return true; }
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwd.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pwd !== confirm) { setErr("Passwords don't match."); return; }
    setLoading(true); setErr("");
    try {
      await authService.resetPassword(token, pwd);
      // sign straight in with the new password: an account with two-step verification required gets the QR set-up next
      const goToApp = async () => { dispatch(setAuthenticated(true)); await dispatch(fetchCurrentUser()); navigate("/dashboard"); };
      try {
        if (!tokenEmail) throw new Error("no email in link");
        const r = await authService.login({ email: tokenEmail, password: pwd, applicationType: "Buyer", isEmail: true });
        if (r?.mfaRequired && r?.mfaToken) { setMfa({ mfaToken: r.mfaToken, setupRequired: !!r.setupRequired, email: tokenEmail }); return; }
        if (r?.status || r?.token) { await goToApp(); return; }
        throw new Error("sign-in");
      } catch {
        // password is saved either way: fall back to the sign-in page
        setDone(true);
        setTimeout(() => navigate("/"), 2500);
      }
    } catch (error: any) {
      // 401: the link's token is no longer accepted (already used once, or older than 24 h)
      setErr(error?.response?.status === 401
        ? "This password-reset link has already been used or has expired. Request a new one from the sign-in page."
        : error?.response?.data?.message || error?.message || "Couldn't reset your password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  const card: React.CSSProperties = { width: "100%", maxWidth: mfa?.setupRequired ? 560 : 420, background: "#fff", borderRadius: 14, boxShadow: "0 10px 40px rgba(16,32,64,.14)", padding: "34px 32px 30px" };
  const input: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "11px 13px", borderRadius: 9, border: "1px solid #d1d5db", fontSize: 14, outline: "none" };
  const label: React.CSSProperties = { display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", margin: "14px 0 6px" };

  return (
    <div style={{ minHeight: "100vh", background: "#f3f5f9", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={card}>
        <img src={LogoDark2x} alt="ProcureX" style={{ height: 30, marginBottom: 22 }} />

        {mfa ? (
          <MfaLoginStep
            mfaToken={mfa.mfaToken}
            setupRequired={mfa.setupRequired}
            email={mfa.email}
            onBack={() => navigate("/")}
            onSuccess={async () => { dispatch(setAuthenticated(true)); await dispatch(fetchCurrentUser()); navigate("/dashboard"); }}
          />
        ) : done ? (
          <>
            <div style={{ fontSize: 19, fontWeight: 700, color: "#1f2a37", marginBottom: 8 }}>Password updated</div>
            <div style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.6 }}>
              Your password has been changed. Redirecting you to sign in…
            </div>
          </>
        ) : !token || tokenExpired ? (
          <>
            <div style={{ fontSize: 19, fontWeight: 700, color: "#1f2a37", marginBottom: 8 }}>Link expired</div>
            <div style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.6, marginBottom: 20 }}>
              This password-reset link is invalid or has expired. Request a new one from the sign-in page.
            </div>
            <button onClick={() => navigate("/")} style={{ width: "100%", padding: "12px 16px", borderRadius: 9, border: "none", background: "#2b3fa0", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              Back to sign in
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <div style={{ fontSize: 19, fontWeight: 700, color: "#1f2a37", marginBottom: 6 }}>{invite ? "Welcome - set your password" : "Set a new password"}</div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.55 }}>
              {tokenEmail ? <>For <strong>{tokenEmail}</strong>. </> : null}
              Use at least 8 characters with 3 of: upper case, lower case, number, symbol.
              {invite ? " Next you will scan a QR code with an authenticator app to secure your account." : ""}
            </div>

            <label style={label}>New password</label>
            <input style={input} type={show ? "text" : "password"} value={pwd} onChange={(e) => { setPwd(e.target.value); setErr(""); }} placeholder="Enter a new password" autoFocus />

            <label style={label}>Confirm password</label>
            <input style={input} type={show ? "text" : "password"} value={confirm} onChange={(e) => { setConfirm(e.target.value); setErr(""); }} placeholder="Re-enter the password" />

            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#6b7280", marginTop: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show password
            </label>

            {err && <div style={{ color: "#dc2626", fontSize: 12.5, marginTop: 12 }}>{err}</div>}

            <button type="submit" disabled={loading} style={{ marginTop: 18, width: "100%", padding: "12px 16px", borderRadius: 9, border: "none", background: "#2b3fa0", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "default" : "pointer", opacity: loading ? 0.75 : 1 }}>
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
